import React, { useEffect } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useGateValue } from '@statsig/react-bindings'
import { AppState } from 'redux/config/store'
import { employeePlatformActions } from 'redux/employeePlatform/employeePlatformSlice'
import { transitPassActions } from 'redux/transitPass/transitPassActions'
import { Locations } from 'shared/Router/Locations'
import { FinchEmploymentTypes } from 'redux/employeePlatform/employeePlatformTypes'
import GridCard from 'employee-platform/shared/components/GridCard'
import Text from 'core-system/Text'
import Button from 'core-system/Button'
import CreditCardIcon from 'core-system/Icons/Accounts/CreditCard'
import StarIcon from 'core-system/Icons/Misc/Star'
import palette from 'core-system/Themes/palette'
import { Card } from 'redux/employeePlatform/employeePlatformTypes'
import {
  cardLast4,
  monthlyBudgetRows,
  walletTransitCards,
  BudgetRow,
  WalletTransitCard,
} from '../dashboardSummary'
import { useDashboardMoney } from '../useDashboardMoney'
import InfoTooltip from 'employee-platform/shared/components/InfoTooltip'
import { PRORATED_BUDGET_TIP } from 'employee-platform/shared/programBudget'

const PinkAction = styled(Button)`
  &&& {
    color: ${(props) => props.theme.palette.primary.pink800};
    padding: 0;
    min-height: 0;
  }

  &&&:hover {
    background-color: transparent;
    color: ${(props) => props.theme.palette.primary.pink600};
  }
`

const CardList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
  gap: 0.75rem;
`

const CardRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 0.75rem;
  background: ${(props) => props.theme.palette.grey.grey50};
  min-width: 0;
`

const IconCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 999px;
  background: ${(props) => props.theme.palette.white};
  flex-shrink: 0;
  overflow: hidden;
`

const AgencyIcon = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`

const CardCopy = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`

const Balance = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.125rem;
  flex-shrink: 0;
`

const BudgetSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1.25rem;
`

const BudgetLabel = styled.div`
  letter-spacing: 0.04em;
`

const BudgetRowBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const BudgetHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
`

const BudgetAmount = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

const Track = styled.div`
  height: 0.375rem;
  border-radius: 999px;
  background: ${(props) => props.theme.palette.grey.grey200};
  overflow: hidden;
`

const Fill = styled.div<{ percent: number; barColor: string }>`
  height: 100%;
  width: ${(props) => props.percent}%;
  background: ${(props) => props.barColor};
  border-radius: 999px;
`

const budgetColor = (kind: string): string => {
  switch (kind) {
    case 'QTF':
      return palette.success.green500
    case 'FLEX':
      return palette.secondary.purple400
    case 'MICROMOBILITY':
      return palette.accent.blue400
    case 'GRH':
      return palette.warning.yellow500
    case 'OTP':
      return palette.grey.grey500
    default:
      return palette.primary.pink400
  }
}

type MoneyFormat = (cents: number) => string

const maskedLast4 = (value: string | null | undefined): string | null => {
  const last4 = cardLast4(value)
  return last4 ? `**** ${last4}` : null
}

const budgetFraction = (row: BudgetRow, money: MoneyFormat): string => {
  const total = row.spent + row.remaining
  if (row.unit === 'trips') {
    const noun = total === 1 ? 'trip' : 'trips'
    return `${row.spent} / ${total} ${noun}`
  }
  return `${money(row.spent)} / ${money(total)}`
}

const spentShare = (row: BudgetRow): number => {
  const total = row.spent + row.remaining
  if (total <= 0) return 0
  return Math.min(100, Math.round((row.spent / total) * 100))
}

const FleetCardRow = ({
  card,
  walletBalance,
  onReload,
  money,
}: {
  card: Card
  walletBalance: number
  onReload: () => void
  money: MoneyFormat
}) => {
  const last4 = maskedLast4(card.last4)
  return (
    <CardRow>
      <IconCircle>
        <CreditCardIcon color={palette.primary.pink800} />
      </IconCircle>
      <CardCopy>
        <Text variant='action3'>Fleet Card</Text>
        <Text variant='body2' textColor={palette.text.secondary}>
          {last4 || 'Card number unavailable'}
        </Text>
      </CardCopy>
      <Balance>
        <Text variant='action3'>{money(walletBalance)}</Text>
        <PinkAction variant='tertiary' size='small' onClick={onReload}>
          Reload
        </PinkAction>
      </Balance>
    </CardRow>
  )
}

const RewardsCardRow = ({
  balance,
  money,
}: {
  balance: number
  money: MoneyFormat
}) => (
  <CardRow>
    <IconCircle>
      <StarIcon color={palette.primary.pink800} />
    </IconCircle>
      <CardCopy>
        <Text variant='action3'>Rewards</Text>
      </CardCopy>
    <Balance>
      <Text variant='action3'>{money(balance)}</Text>
    </Balance>
  </CardRow>
)

const TransitCardRow = ({
  card,
  onReload,
  money,
}: {
  card: WalletTransitCard
  onReload: () => void
  money: MoneyFormat
}) => {
  const balance = card.balanceCents == null ? '—' : money(card.balanceCents)
  const last4 = maskedLast4(card.cardNumber)
  return (
    <CardRow>
      <IconCircle>
        {card.iconUrl ? (
          <AgencyIcon src={card.iconUrl} alt='' />
        ) : (
          <CreditCardIcon color={palette.primary.pink800} />
        )}
      </IconCircle>
      <CardCopy>
        <Text variant='action3'>{card.name}</Text>
        {last4 ? (
          <Text variant='body2' textColor={palette.text.secondary}>
            {last4}
          </Text>
        ) : null}
      </CardCopy>
      <Balance>
        <Text variant='action3'>{balance}</Text>
        <PinkAction variant='tertiary' size='small' onClick={onReload}>
          Reload
        </PinkAction>
      </Balance>
    </CardRow>
  )
}

const MyWalletCard = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const hideFleetWallet = useGateValue('show_kc_welcome_message')
  const money = useDashboardMoney()
  const { wallet, programs, preTaxPrograms, profileData } = useSelector(
    (state: AppState) => state.employeePlatform
  )
  const transitPasses = useSelector(
    (state: AppState) => state.transitPass.employeeTransitPasses
  )

  useEffect(() => {
    dispatch(transitPassActions.getEmployeeTransitPasses())
  }, [dispatch])

  const openSettings = () => navigate(Locations.EmployeeSettings)
  const openTransitPass = () => navigate(Locations.EmployeeTransitPass.Overview)

  const programList = programs?.allPrograms
    ? Object.values(programs.allPrograms).flatMap((typePrograms) =>
        Object.values(typePrograms)
      )
    : []
  const budgets = monthlyBudgetRows({
    programs: programList,
    transit: preTaxPrograms?.COMMUTER_TRANSIT ?? null,
    parking: preTaxPrograms?.COMMUTER_PARKING ?? null,
    isContractor:
      profileData?.finchEmploymentType === FinchEmploymentTypes.CONTRACTOR,
  })
  const fleetCards = hideFleetWallet ? [] : wallet?.cards ?? []
  const fleetCardBalance = wallet?.currentBalance ?? 0
  const rewardsBalance = wallet?.rewards?.balance ?? 0
  const transitCards = walletTransitCards(programList, transitPasses)
  const hasCards =
    fleetCards.length > 0 || transitCards.length > 0 || rewardsBalance > 0

  return (
    <GridCard
      headerTitle='My Wallet'
      headerButtons={
        hideFleetWallet ? null : (
          <PinkAction variant='tertiary' size='small' onClick={openSettings}>
            + Add Card
          </PinkAction>
        )
      }
    >
      <CardList>
        {fleetCards.map((card) => (
          <FleetCardRow
            key={card.internalId}
            card={card}
            walletBalance={fleetCardBalance}
            onReload={openSettings}
            money={money}
          />
        ))}
        {rewardsBalance > 0 ? (
          <RewardsCardRow balance={rewardsBalance} money={money} />
        ) : null}
        {transitCards.map((card) => (
          <TransitCardRow key={card.id} card={card} onReload={openTransitPass} money={money} />
        ))}
        {!hasCards ? (
          <Text variant='body2' textColor={palette.text.secondary}>
            No cards on your account yet.
          </Text>
        ) : null}
      </CardList>
      {budgets.length > 0 ? (
        <BudgetSection>
          <BudgetLabel>
            <Text variant='caption' textColor={palette.text.secondary}>
              MONTHLY BUDGETS
            </Text>
          </BudgetLabel>
          {budgets.map((row) => (
            <BudgetRowBlock key={row.id}>
              <BudgetHeader>
                <Text variant='action4'>{row.name}</Text>
                <BudgetAmount>
                  <Text variant='action4'>{budgetFraction(row, money)}</Text>
                  {row.prorated ? (
                    <InfoTooltip
                      label='Prorated budget'
                      message={PRORATED_BUDGET_TIP}
                    />
                  ) : null}
                </BudgetAmount>
              </BudgetHeader>
              <Track>
                <Fill percent={spentShare(row)} barColor={budgetColor(row.kind)} />
              </Track>
              {row.resetsInDays != null ? (
                <Text variant='body2' textColor={palette.text.secondary}>
                  Resets in {row.resetsInDays}{' '}
                  {row.resetsInDays === 1 ? 'day' : 'days'}
                </Text>
              ) : null}
            </BudgetRowBlock>
          ))}
        </BudgetSection>
      ) : null}
    </GridCard>
  )
}

export default MyWalletCard
